/*
 * Auth service
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders
} from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  of,
  Subject
} from 'rxjs';
import { UserService } from 'app/shared/services/user.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  catchError,
  map
} from 'rxjs/operators';
import { Constants } from 'app/shared/config/constants';
import { ClientService } from 'app/shared/services/client.service';
import { TokenService } from 'app/shared/services/token.service';
import { AiTokenService } from 'app/shared/services/ai-token.service';
import { UiConfigService } from 'app/shared/services/ui-config.service';
import { ContactEmailService } from 'app/shared/services/contact-email.service';

export interface AuthResponse {
  accessToken: string;
  type?: 'AUTH' | 'MFA';
  mfaRedirectUrl?: string;
}

@Injectable ( {
  providedIn: 'root',
} )
export class AuthService {
  public uri = Constants.authServiceUrl;
  private loginChangedSubject = new Subject<boolean> ();
  public loginChanged = this.loginChangedSubject.asObservable ();

  constructor (
    private http: HttpClient,
    private userService: UserService,
    private jwtHelper: JwtHelperService,
    private clientService: ClientService,
    private tokenService: TokenService,
    private uiConfigService: UiConfigService,
    private aiTokenService: AiTokenService,
    private contactEmailService: ContactEmailService,
  ) {
  }

  public get isAuthenticated (): boolean {
    return !!this.tokenService.token;
  }

  public get accessToken (): string | Promise<string> {
    return this.jwtHelper.tokenGetter ();
  }

  public get authenticatedUiConfigUrlSegmentKey (): string | null {
    return this.localStorageUiConfigUrlSegmentKey || null;
  }

  public async invalidateAuthIfUiConfigMismatch ( urlSegmentKey?: string | null ): Promise<boolean> {
    if ( !this.isAuthenticated || this.isAuthenticatedForUiConfig ( urlSegmentKey ) ) {
      return false;
    }

    await this.setAuthenticated ( false );
    return true;
  }

  public async setAuthenticated ( value: boolean, accessToken?: string, urlSegmentKey?: string | null ): Promise<boolean> {
    return new Promise ( async ( resolve, reject ) => {
      if ( value ) {
        localStorage.removeItem ( Constants.localStorageKeys.___gbru );
        try {
          this.tokenService.token = accessToken;
          await this.userService.getUser ( this.tokenService.token.sub )
            .toPromise ();
          await this.clientService.getBUSettings ();
          this.storeAuthenticatedUiConfigUrlSegmentKey ( urlSegmentKey );
          this.loginChangedSubject.next ( true );
          resolve ( true );
        } catch ( e ) {
          reject ( e );
        }
      } else {
        this.tokenService.delete ();
        this.aiTokenService.deleteCookie ();
        this.userService.unsetUser ();
        this.contactEmailService.clear ();
        this.loginChangedSubject.next ( false );
        localStorage.removeItem ( Constants.localStorageKeys.fedEmailDirectMessageId );
        localStorage.removeItem ( Constants.localStorageKeys.reporterAuthUiConfigUrlSegmentKey );
        resolve ( false );
      }
    } );
  }

  public async login ( username: string, password: string, handOffApplication?: string | null ): Promise<boolean | AuthResponse | undefined> {
    try {
      const headers = new HttpHeaders ( {
        Authorization: `Basic ${ btoa ( username + ':' + password ) }`,
      } );
      const loginUrl = handOffApplication
        ? `${ this.uri }/login?application=${ encodeURIComponent ( handOffApplication ) }`
        : `${ this.uri }/login`;

      const loginResponse = await this.http.post<AuthResponse> ( loginUrl, {}, { headers } )
        .toPromise ();

      if ( loginResponse ) {
        if ( loginResponse.type === 'MFA' || handOffApplication ) {
          return loginResponse;
        }

        return await this.setAuthenticated ( true, loginResponse.accessToken );
      }
    } catch ( err ) {
      return Promise.reject ( err );
    }
  }

  public async oidcLogin ( token: string, urlSegmentKey?: string | null ) {
    const decodedToken = this.jwtHelper.decodeToken ( token );
    if ( decodedToken?.sub ) {
      return await this.setAuthenticated ( true, token, urlSegmentKey );
    }
    return false;
  }

  public async exchangeMfaToken ( mfaJwt: string, ssoToken: string, urlSegmentKey?: string | null ): Promise<boolean> {
    const accessToken = await this.requestMfaExchange ( mfaJwt, ssoToken );

    try {
      return await this.setAuthenticated ( true, accessToken, urlSegmentKey );
    } catch ( e ) {
      await this.setAuthenticated ( false );
      throw e;
    }
  }

  public async exchangeMfaTokenForHandOff ( mfaJwt: string, ssoToken: string ): Promise<string> {
    return this.requestMfaExchange ( mfaJwt, ssoToken );
  }

  private async requestMfaExchange ( mfaJwt: string, ssoToken: string ): Promise<string> {
    const headers = new HttpHeaders ( {
      Authorization: `Bearer ${ mfaJwt }`,
      'Content-Type': 'text/plain',
    } );

    const response = await this.http.post<AuthResponse> ( Constants.mfaTokenExchangeUrl, ssoToken, { headers } )
      .toPromise ();

    if ( !response?.accessToken?.trim () || response.type === 'MFA' ) {
      throw new Error ( 'Invalid MFA exchange response' );
    }

    return response.accessToken;
  }

  public logout (): Observable<any> {
    if ( this.isAuthenticated ) {
      const accessToken = this.accessToken;
      const headers = new HttpHeaders ( { Authorization: `Bearer ${ accessToken }` } );
      this.setAuthenticated ( false )
        .then ();
      if ( !accessToken ) {
        return of ();
      }
      return this.http.post ( `${ this.uri }/logout`, {}, { headers } )
        .pipe (
          map ( ( r => {
            return r;
          } ) ),
          catchError ( () => of ( null ) ),
        );
    } else {
      return of ();
    }
  }

  private isAuthenticatedForUiConfig ( urlSegmentKey?: string | null ): boolean {
    return this.normalizeUiConfigUrlSegmentKey ( this.authenticatedUiConfigUrlSegmentKey ) ===
      this.normalizeUiConfigUrlSegmentKey ( urlSegmentKey );
  }

  private storeAuthenticatedUiConfigUrlSegmentKey ( urlSegmentKey?: string | null ) {
    localStorage.setItem (
      Constants.localStorageKeys.reporterAuthUiConfigUrlSegmentKey,
      this.normalizeUiConfigUrlSegmentKey ( urlSegmentKey ?? this.uiConfigService.urlSegmentKey )
    );
  }

  private normalizeUiConfigUrlSegmentKey ( urlSegmentKey?: string | null ): string {
    return urlSegmentKey?.trim () || '';
  }

  private get localStorageUiConfigUrlSegmentKey (): string {
    return localStorage.getItem ( Constants.localStorageKeys.reporterAuthUiConfigUrlSegmentKey ) || '';
  }
}
